
import { Validator, ValidationHelpers } from './validation.js';

// Use global logger to avoid circular dependencies
const log = typeof window !== 'undefined' && window.Log ? window.Log('favorites') : {
  blue: () => {}, red: () => {}, green: () => {}, yellow: () => {}, purple: () => {}, log: () => {}, table: () => {}
};

const STORAGE_KEY = 'cocktail-favorites';

/**
 * Favorites utility for managing favorite cocktails in localStorage
 */
export class Favorites {
  
  /**
   * Get all favorite cocktail IDs from localStorage with validation
   * @returns {Set<string>} Set of validated favorite cocktail IDs
   */
  static getFavorites() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return ValidationHelpers.safeStorageGet(STORAGE_KEY) || new Set();
    } catch (error) {
      log.red('Error loading favorites:', error);
      return new Set();
    }
  }

  /**
   * Save favorites to localStorage with validation
   * @param {Set<string>} favorites Set of favorite cocktail IDs
   */
  static saveFavorites(favorites) {
    try {
      if (!(favorites instanceof Set)) {
        log.yellow('Invalid favorites data type, converting to Set');
        favorites = new Set(favorites);
      }
      
      // Validate each ID before saving
      const validatedIds = [];
      for (const id of favorites) {
        try {
          const validatedId = Validator.validateCocktailId(id);
          validatedIds.push(validatedId);
        } catch (error) {
          log.yellow(`Skipping invalid favorite ID "${id}":`, error.message);
        }
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedIds));
      log.green('Favorites saved:', validatedIds.length, 'valid items');
      
      if (validatedIds.length !== favorites.size) {
        log.yellow(`Filtered out ${favorites.size - validatedIds.length} invalid favorite IDs`);
      }
    } catch (error) {
      log.red('Error saving favorites:', error);
    }
  }

  /**
   * Check if a cocktail is favorited with validation
   * @param {string} cocktailId The cocktail ID
   * @returns {boolean} True if cocktail is favorited
   */
  static isFavorite(cocktailId) {
    try {
      const validatedId = Validator.validateCocktailId(cocktailId);
      const favorites = this.getFavorites();
      return favorites.has(validatedId);
    } catch (error) {
      log.yellow('Invalid cocktail ID for favorite check:', error.message);
      return false;
    }
  }

  /**
   * Add a cocktail to favorites with validation
   * @param {string} cocktailId The cocktail ID
   * @returns {boolean} True if added, false if already existed or invalid
   */
  static addFavorite(cocktailId) {
    try {
      const validatedId = Validator.validateCocktailId(cocktailId);
      
      const favorites = this.getFavorites();
      if (favorites.has(validatedId)) {
        return false; // Already exists
      }
      
      favorites.add(validatedId);
      this.saveFavorites(favorites);
      
      // Dispatch event for other components to listen
      document.dispatchEvent(new CustomEvent('favorite-added', {
        detail: { cocktailId: validatedId }
      }));
      
      log.green('Added to favorites:', validatedId);
      return true;
    } catch (error) {
      log.red('Invalid cocktail ID for add favorite:', error.message);
      return false;
    }
  }

  /**
   * Remove a cocktail from favorites with validation
   * @param {string} cocktailId The cocktail ID
   * @returns {boolean} True if removed, false if didn't exist or invalid
   */
  static removeFavorite(cocktailId) {
    try {
      const validatedId = Validator.validateCocktailId(cocktailId);
      
      const favorites = this.getFavorites();
      if (!favorites.has(validatedId)) {
        return false; // Doesn't exist
      }
      
      favorites.delete(validatedId);
      this.saveFavorites(favorites);
      
      // Dispatch event for other components to listen
      document.dispatchEvent(new CustomEvent('favorite-removed', {
        detail: { cocktailId: validatedId }
      }));
      
      log.green('Removed from favorites:', validatedId);
      return true;
    } catch (error) {
      log.red('Invalid cocktail ID for remove favorite:', error.message);
      return false;
    }
  }

  /**
   * Toggle a cocktail's favorite status with validation
   * @param {string} cocktailId The cocktail ID
   * @returns {boolean} True if now favorited, false if unfavorited or invalid
   */
  static toggleFavorite(cocktailId) {
    try {
      const validatedId = Validator.validateCocktailId(cocktailId);
      
      if (this.isFavorite(validatedId)) {
        this.removeFavorite(validatedId);
        return false;
      } else {
        this.addFavorite(validatedId);
        return true;
      }
    } catch (error) {
      log.red('Invalid cocktail ID for toggle favorite:', error.message);
      return false;
    }
  }

  /**
   * Get count of favorites
   * @returns {number} Number of favorites
   */
  static getFavoriteCount() {
    return this.getFavorites().size;
  }

  /**
   * Clear all favorites
   */
  static clearFavorites() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      document.dispatchEvent(new CustomEvent('favorites-cleared'));
      log.green('All favorites cleared');
    } catch (error) {
      log.red('Error clearing favorites:', error);
    }
  }
}
